import React, { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  FormFieldFormTemplateMappingSource,
  FormTemplateMappingFormValues,
  FormTemplateMappingSource,
  FormTemplateMappingTarget,
} from '../../../../models/FormTemplate'
import { Descriptor, UiSchemaDescriptor } from '../../../../models/Scorecard'

import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'

import { doSourcesMatch, formTemplateMappingTargetToSelectOption, mappingExistsForSource, mappingExistsForTarget } from '../utils'

import { FormFieldFormTemplateMappingSourceColumn } from './FormTemplateMappingSourceColumn'
import { FormTemplateMappingTargetColumn } from './FormTemplateMappingTargetColumn'

const _DEFAULT_MAPPING_TYPE_OPTION: SelectOption = {
  id: 'default',
  label: 'Match each choice to a PDF field',
}

const _SEPARATOR_MAPPING_TYPE_OPTION: SelectOption = {
  id: 'separator',
  label: 'Or match choices to values from a PDF field',
  className: 'py-2 lead-extra-small zocks-bg-color-white zocks-text-gray-500 pe-none',
  disabled: true,
}

const getInitialSelectedParentTargetOption = (
  parentTargets: FormTemplateMappingTarget[],
  targets: FormTemplateMappingTarget[],
  mapping: Record<string, FormTemplateMappingSource>,
  source: FormFieldFormTemplateMappingSource,
  descriptorOptions: string[],
): SelectOption | null => {
  const selectedParentTarget = parentTargets.find((parentTarget) => {
    const childTargets = targets.filter(({ parentId }) => parentId === parentTarget.id)
    if (!childTargets.length) {
      return false
    }

    return childTargets.some((childTarget) =>
      descriptorOptions.some((descriptorOption) => {
        if (!mapping[childTarget.id]) {
          return false
        }

        return doSourcesMatch(mapping[childTarget.id], { ...source, option: descriptorOption })
      }),
    )
  })

  if (!selectedParentTarget) {
    return null
  }

  return { id: selectedParentTarget.id, label: selectedParentTarget.name }
}

interface SingleChoiceFormFieldFormTemplateMappingRowsProps {
  descriptor: Descriptor
  descriptorOptions: string[]
  uiSchemaDescriptor?: UiSchemaDescriptor
  targets: FormTemplateMappingTarget[]
  source: FormFieldFormTemplateMappingSource
  showUnmappedOnly: boolean
}

export const SingleChoiceFormFieldFormTemplateMappingRows: React.FC<SingleChoiceFormFieldFormTemplateMappingRowsProps> = ({
  descriptor,
  descriptorOptions,
  uiSchemaDescriptor,
  targets,
  source,
  showUnmappedOnly,
}) => {
  const { watch, setValue } = useFormContext<FormTemplateMappingFormValues>()

  const mapping = watch('mapping')

  const filteredDescriptorOptions = showUnmappedOnly
    ? descriptorOptions.filter((option) => !mappingExistsForSource({ ...source, option }, mapping))
    : descriptorOptions

  const { flatTargets, parentTargets } = useMemo(() => {
    const parentIds = new Set(targets.map(({ parentId }) => parentId))
    return {
      flatTargets: targets.filter(({ parentId, id: targetId }) => !parentId && !parentIds.has(targetId)),
      parentTargets: targets.filter(({ id: targetId }) => parentIds.has(targetId)),
    }
  }, [targets])

  const parentTargetOptions = useMemo(
    () => parentTargets.filter((target) => !mappingExistsForTarget(target, targets, mapping)).map(formTemplateMappingTargetToSelectOption),
    [mapping, parentTargets, targets],
  )

  const [selectedMappingModeOption, setSelectedMappingModeOption] = useState<SelectOption>(
    () => getInitialSelectedParentTargetOption(parentTargets, targets, mapping, source, descriptorOptions) ?? _DEFAULT_MAPPING_TYPE_OPTION,
  )

  const childTargets = useMemo(
    () => (selectedMappingModeOption.id === 'default' ? [] : targets.filter((target) => target.parentId === selectedMappingModeOption.id)),
    [selectedMappingModeOption, targets],
  )

  const handleMappingModeOptionSelected = (option: SelectOption) => {
    const childTargetIds = childTargets.map((childTarget) => childTarget.id)
    if (childTargetIds.length) {
      const mappingEntriesWithoutChildTargetIds = Object.entries(mapping).filter(([targetId]) => !childTargetIds.includes(targetId))
      setValue('mapping', Object.fromEntries(mappingEntriesWithoutChildTargetIds))
    }
    setSelectedMappingModeOption(option)
  }

  return (
    <>
      <div className='d-flex align-items-center border-top zocks-border-color-gray-200 form-template-mapping-row'>
        <FormFieldFormTemplateMappingSourceColumn descriptor={descriptor} uiSchemaDescriptor={uiSchemaDescriptor} showSpacer={true} />
        <div className='w-50 d-flex flex-column gap-3 px-3 zocks-py-0.75'>
          <SelectDropdown
            options={[_DEFAULT_MAPPING_TYPE_OPTION, ...(parentTargetOptions.length ? [_SEPARATOR_MAPPING_TYPE_OPTION, ...parentTargetOptions] : [])]}
            onOptionSelected={handleMappingModeOptionSelected}
            initialSelectedOption={selectedMappingModeOption}
            DropDownItemOptionComponent={DropDownItemOptionComponent}
          />
        </div>
      </div>
      {filteredDescriptorOptions.map((option) => (
        <div
          key={`single-choice-form-field-form-template-mapping-row-${source.path}-${option}`}
          className='d-flex align-items-center zocks-border-color-gray-200 form-template-mapping-row'
        >
          <FormFieldFormTemplateMappingSourceColumn descriptor={descriptor} uiSchemaDescriptor={uiSchemaDescriptor} option={option} />
          <FormTemplateMappingTargetColumn targets={selectedMappingModeOption.id === 'default' ? flatTargets : childTargets} source={{ ...source, option }} />
        </div>
      ))}
    </>
  )
}

export const DropDownItemOptionComponent: React.FC<DropDownItemOptionComponentProps> = ({ optionLabel, onClick, disabled = false, className = '' }) => {
  return (
    <CustomDropdownItemOption role='button' onClick={disabled ? undefined : onClick} className={className}>
      {optionLabel}
    </CustomDropdownItemOption>
  )
}
